// Angular
import { Injectable } from '@angular/core';
// Sore
import { Selector, State, Action, StateContext } from '@ngxs/store';
// Actions
import {
	ChangeEventStatusChannelAction,
	ChangeEventItemOnChannelAction,
	LastConnection,
	RegisterChannel,
	RegisterEventChannelAction,
	UnregisterChannel, SetOfflineConnection, SaveConnectionID, RemoveEventChannelAction,
	SetAvailabilityConnection
} from './action/signal-r.action';
import { Logout } from '../auth/action/auth.action';
// Service
import { ObjectUtils } from '../utils/object.utils';
// Model
import {
	ChannelConfigModel,
	EVENT_STATUS,
	SIGNAL_STATUS,
	SignalRModel
} from './model/signal-r.model';

@State<SignalRModel>({
	name: 'signalR',
	defaults: {
		channels: {},
		lastConnection: null,
		connectionId: null,
		status: SIGNAL_STATUS.OFFLINE,
		enabled: false
	}
})
@Injectable()
export class SignalRState {

	/**
	 * Returns INVisionAppState state.
	 * @param state
	 * @constructor
	 */
	@Selector()
	static SignalRState(state: SignalRModel): SignalRModel {
		return state;
	}

	/**
	 * Return any changes to Events
	 * @param state
	 */
	@Selector()
	static channelEvents(state: SignalRModel): any {
		return state.channels;
	}

	/**
	 * Return Last Connection
	 * @param state
	 */
	@Selector()
	static lastConnection(state: SignalRModel): any {
		return state.lastConnection;
	}

	/**
	 * Return Last Connection ID
	 * @param state
	 */
	@Selector()
	static LastConnectionID(state: SignalRModel): any {
		return state.connectionId;
	}

	/**
	 * Return Connection Status
	 * @param state
	 */
	@Selector()
	static connectionStatus(state: SignalRModel): any {
		return state.status;
	}

	/**
	 * Save Last Connection timestamp
	 * @param ctx
	 * @param channelName
	 */
	@Action(LastConnection)
	saveLastConnection(ctx: StateContext<SignalRModel>, {timestamp}: LastConnection): any {
		const state = ctx.getState();
		ctx.patchState({
			...state,
			lastConnection: timestamp,
			status: SIGNAL_STATUS.ONLINE,
			enabled: true
		});
	}

	/**
	 * Save the Last Connection ID to the Signal R
	 * @param ctx
	 * @param connectionId
	 */
	@Action(SaveConnectionID)
	saveConnectionID(ctx: StateContext<SignalRModel>, {connectionId}: SaveConnectionID): any {
		const state = ctx.getState();
		ctx.patchState({
			...state,
			connectionId: connectionId
		});
	}

	/**
	 * Set the Connection to Offline
	 * @param ctx
	 */
	@Action(SetOfflineConnection)
	setOfflineConnection(ctx: StateContext<SignalRModel>): any {
		const state = ctx.getState();
		ctx.patchState({
			...state,
			status: SIGNAL_STATUS.OFFLINE
		});
	}

	/**
	 * Action handler for updating the availability connection state.
	 *
	 * @param {StateContext<SignalRModel>} ctx - The state context for the SignalR model.
	 * @param {SetAvailabilityConnection} param1 - The action payload containing the availability connection state.
	 * @param {boolean} param1.enabled - The new availability connection state to be updated.
	 * @return {any} Returns the updated state after modifying the availability connection status.
	 */
	@Action(SetAvailabilityConnection)
	SetAvailabilityConnection(ctx: StateContext<SignalRModel>, { enabled }: SetAvailabilityConnection): any {
		const state = ctx.getState();
		ctx.patchState({
			...state,
			enabled: enabled
		});
	}

	/**
	 * Register a new channel only if this was not already in the list
	 * @param ctx
	 * @param channelName
	 */
	@Action(RegisterChannel)
	registerNewChannel(ctx: StateContext<SignalRModel>, {channelName, channelConfigModel}: RegisterChannel): any {
		const state = ctx.getState();
		if (!state.channels[channelName]) {
			const newChannels = Object.assign({}, state.channels);

			newChannels[channelName] = channelConfigModel;

			ctx.patchState({
				...state,
				channels: newChannels
			});
		}
	}

	/**
	 * Unregister a channel if exists
	 * @param ctx
	 * @param channelName
	 */
	@Action(UnregisterChannel)
	unregisterChannel(ctx: StateContext<SignalRModel>, {channelName}: UnregisterChannel): any {
		const state = ctx.getState();
		if (state.channels[channelName]) {
			const channels = Object.assign({}, state.channels);
			delete channels[channelName];

			ctx.patchState({
				...state,
				channels: channels
			});
		}
	}

	/**
	 * Save the last event action of a channel using the MAX_NUMBER_EVENTS
	 * @param ctx
	 * @param channelName
	 * @param event
	 */
	@Action(RegisterEventChannelAction)
	registerEventChannelAction(ctx: StateContext<SignalRModel>, {channelName, event}: RegisterEventChannelAction): any {
		const state = ctx.getState();
		if (state.channels[channelName]) {
			const channels = ObjectUtils.recursiveDeepCopy(state.channels);
			const channelConfigModel: ChannelConfigModel = channels[channelName];
			const channelEvents = [].concat(channelConfigModel.events || []);

			event['timestamp'] = Date.now();
			event['status'] = EVENT_STATUS.NEW;

			channelEvents.unshift(event);

			if (channelEvents.length > channelConfigModel.streamLimit) {
				channelEvents.pop();
			}
			channels[channelName].events = channelEvents;

			ctx.patchState({
				...state,
				channels: channels
			});
		}
	}

	/**
	 * Remove one event by id from a specific channel
	 * @param ctx
	 * @param channelName
	 * @param event
	 */

	@Action(RemoveEventChannelAction)
	removeEventChannelAction(ctx: StateContext<SignalRModel>, {channelName, event, parameter}: RemoveEventChannelAction): any {
		const state = ctx.getState();
		if (state.channels[channelName]) {
			const channels = ObjectUtils.recursiveDeepCopy(state.channels);
			const channel = [].concat(channels[channelName].events);

			//😏 example: channel[0].data['order'].id
			channels[channelName].events = [...channel.filter(e => e.data[parameter].id !== event.data[parameter].id)];

			ctx.patchState({
				...state,
				channels: channels
			});
		}
	}

	/**
	 * Change the status over an specific channel
	 * @param ctx
	 * @param channelName
	 * @param event
	 */
	@Action(ChangeEventStatusChannelAction)
	changeEventStatusOnChannel(ctx: StateContext<SignalRModel>, {channelName, status}: ChangeEventStatusChannelAction): any {
		const state = ctx.getState();
		if (state.channels[channelName]) {
			const channels = ObjectUtils.recursiveDeepCopy(state.channels);
			const channelConfigModel: ChannelConfigModel = channels[channelName];
			const channelEvents = [].concat(channelConfigModel.events || []);

			const newChannelChanges: any = [];

			channelEvents.forEach((item: any) => {
				const newItem = Object.assign({}, item);
				newItem['status'] = status;
				newChannelChanges.push(newItem);
			});

			channels[channelName].events = newChannelChanges;

			ctx.patchState({
				...state,
				channels: channels
			});
		}
	}

	/**
	 * Change the information of an specific Event, using a parameter
	 * @param ctx
	 * @param channelName
	 * @param event
	 */
	@Action(ChangeEventItemOnChannelAction)
	changeEventItemOnChannel(ctx: StateContext<SignalRModel>, {channelName, event, parameter}: ChangeEventItemOnChannelAction): any {
		const state = ctx.getState();
		if (state.channels[channelName]) {
			const channels = ObjectUtils.recursiveDeepCopy(state.channels);
			const channelConfigModel: ChannelConfigModel = channels[channelName];
			const channelEvents = [].concat(channelConfigModel.events || []);

			const eventItem = channelEvents.find((item: any) => {
				return item.data[parameter] && item.data[parameter] === event.data[parameter];
			});

			// If there is an event item i.e. event['id'] at index 5
			if (eventItem) {
				const newChannelChanges: any = [];
				channelEvents.forEach((item: any) => {
					let newItem = Object.assign({}, item);
					if (item.data[parameter] && item.data[parameter] === event.data[parameter]) {
						newItem = Object.assign({}, event);
						newItem['status'] = EVENT_STATUS.OLD;
					}
					newChannelChanges.push(newItem);
				});
				channels[channelName].events = newChannelChanges;

				ctx.patchState({
					...state,
					channels: channels
				});
			}
		}
	}

	/**
	 * Clear out the Signal R
	 * @param ctx
	 */
	@Action(Logout)
	logout(ctx: StateContext<SignalRModel>): void {
		ctx.setState({
			channels: {},
			lastConnection: null,
			connectionId: null,
			status: SIGNAL_STATUS.OFFLINE,
			enabled: false
		});
	}
}
